import { Card, Center, Stack, Text, ThemeIcon } from '@mantine/core'
import type { ReactNode } from 'react'

export interface EmptyStateProps {
    // The glyph shown in the tinted ThemeIcon, e.g. `<IconUsers size={28} />`.
    // The ThemeIcon chrome (light variant, size 56, md radius) is applied here so
    // every empty state looks identical; the caller only picks the icon + color.
    icon: ReactNode
    title: ReactNode
    description: ReactNode
    // ThemeIcon color (defaults to the primary). e.g. the dashboard uses 'sage'.
    color?: string
    // Optional call-to-action — pass a button node (e.g. a `<CtaButton>`); kept as
    // a node so this shared primitive stays free of feature/navigation deps.
    action?: ReactNode
}

// Shared "nothing here yet" block: a full-height bordered card with a centered
// icon + title + description + optional CTA. Drops into the feature pages' flex
// columns (it grows to fill the area). For error states use an inline error
// card with a Retry instead — those are a different concern, not this.
export function EmptyState({
    icon,
    title,
    description,
    color,
    action,
}: EmptyStateProps) {
    return (
        <Card withBorder style={{ flex: 1 }}>
            <Center h='100%'>
                <Stack align='center' gap='xs' py='xl'>
                    <ThemeIcon
                        variant='light'
                        size={56}
                        radius='md'
                        color={color}
                    >
                        {icon}
                    </ThemeIcon>
                    <Text fw={600}>{title}</Text>
                    <Text c='dimmed' size='sm' ta='center' maw={440}>
                        {description}
                    </Text>
                    {action}
                </Stack>
            </Center>
        </Card>
    )
}
